import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  MessageDefinition,
  MessageEvent,
  MessageInstance,
  ValidationResult,
  MessageTemplate,
  ParameterSet,
  Dataset,
  ResolvedMessageTemplate,
} from '@k-ucap/shared';
import { Direction, GlobalRole, MESSAGE_DICTIONARY_VERSION, Permission, ValidationSeverity } from '@k-ucap/shared';
import { createMessageDictionaryService, MessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { createMessageValidationService, MessageValidationService } from '@/domain/message/MessageValidationService.js';
import { createPreviewMessageEncoder, PreviewMessageEncoder } from '@/domain/message/PreviewMessageEncoder.js';
import {
  getGlobalVirtualMessageAdapter,
  VirtualMessageAdapter,
} from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useMessageStore } from '@/stores/useMessageStore.js';
import { createTemplateResolverService, createDatasetRowSelector } from '@/domain/asset/TemplateResolverService.js';
import { createExpressionResolverService } from '@/domain/asset/ExpressionResolverService.js';
import { createParameterSetService } from '@/domain/asset/ParameterSetService.js';
import { TestAssetType, AssetStatus, DatasetRowStrategy } from '@k-ucap/shared';

const DEMO_DEFAULT_MESSAGE_TYPE = 101;

interface GeneratorState {
  selectedMessage: MessageDefinition | null;
  payload: Record<string, unknown>;
  validationResult: ValidationResult | null;
  rawBytes: Uint8Array | null;
  rawHex: string;
  sequenceNumber: number;
  isSending: boolean;
  recentEvents: MessageEvent[];
  sendError: string | null;
  sendSuccess: { messageType: number; sequenceNumber: number; validation: string } | null;
  snackbarOpen: boolean;
  lastSentEventId: string | null;
  dictionaryService: MessageDictionaryService;
  validationService: MessageValidationService;
  encoder: PreviewMessageEncoder;
  adapter: VirtualMessageAdapter;
  // Asset selection
  availableTemplates: MessageTemplate[];
  availableParameterSets: ParameterSet[];
  availableDatasets: Dataset[];
  selectedTemplate: MessageTemplate | null;
  selectedParameterSet: ParameterSet | null;
  selectedDataset: Dataset | null;
  resolvedPreview: ResolvedMessageTemplate | null;
  resolvedPayloadSource: Array<{ source: string; id: string; field?: string; value?: unknown }>;
  isLoadingAssets: boolean;
}

interface GeneratorActions {
  selectMessage: (message: MessageDefinition) => void;
  updatePayload: (key: string, value: unknown) => void;
  loadDemoValues: () => void;
  resetPayload: () => void;
  validate: () => ValidationResult;
  send: (force?: boolean) => Promise<void>;
  clearSendFeedback: () => void;
  closeSnackbar: () => void;
  canSend: boolean;
  canForceSend: boolean;
  isProjectSelected: boolean;
  // Asset actions
  selectTemplate: (template: MessageTemplate | null) => void;
  selectParameterSet: (parameterSet: ParameterSet | null) => void;
  selectDataset: (dataset: Dataset | null) => void;
  refreshAssets: () => Promise<void>;
  saveAsTemplate: (name: string, description?: string) => Promise<MessageTemplate | null>;
}

export const useGenerator = (): GeneratorState & GeneratorActions => {
  const { messageEventRepository, messageTemplateRepository, parameterSetRepository, datasetRepository } =
    useRepositories();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const authUser = useAuthStore((s) => s.authUser);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addMessageToStore = useMessageStore((s) => s.addMessage);

  const [selectedMessage, setSelectedMessage] = useState<MessageDefinition | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [rawBytes, setRawBytes] = useState<Uint8Array | null>(null);
  const [rawHex, setRawHex] = useState('');
  const [sequenceNumber, setSequenceNumber] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [recentEvents, setRecentEvents] = useState<MessageEvent[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<{
    messageType: number;
    sequenceNumber: number;
    validation: string;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [lastSentEventId, setLastSentEventId] = useState<string | null>(null);

  const [availableTemplates, setAvailableTemplates] = useState<MessageTemplate[]>([]);
  const [availableParameterSets, setAvailableParameterSets] = useState<ParameterSet[]>([]);
  const [availableDatasets, setAvailableDatasets] = useState<Dataset[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [selectedParameterSet, setSelectedParameterSet] = useState<ParameterSet | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [baseTemplate, setBaseTemplate] = useState<MessageTemplate | null>(null);
  const [resolvedPreview, setResolvedPreview] = useState<ResolvedMessageTemplate | null>(null);
  const [resolvedPayloadSource, setResolvedPayloadSource] = useState<
    Array<{ source: string; id: string; field?: string; value?: unknown }>
  >([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  const services = useMemo(
    () => ({
      dictionaryService: createMessageDictionaryService(),
      validationService: createMessageValidationService(),
      encoder: createPreviewMessageEncoder(),
      adapter: getGlobalVirtualMessageAdapter(),
      expressionResolver: createExpressionResolverService(),
      templateResolver: createTemplateResolverService(createExpressionResolverService()),
      parameterSetService: createParameterSetService(),
      datasetRowSelector: createDatasetRowSelector(),
    }),
    []
  );

  const unsubscribeRef = useRef<(() => void) | null>(null);

  const canSend = Boolean(selectedProject && hasPermission(Permission.MESSAGE_GENERATE));
  const canForceSend = Boolean(
    userProfile?.globalRole === GlobalRole.ADMIN || hasPermission(Permission.MESSAGE_GENERATE)
  );
  const isProjectSelected = Boolean(selectedProject);

  const buildDefaultPayload = useCallback((message: MessageDefinition): Record<string, unknown> => {
    const defaults: Record<string, unknown> = {};
    for (const field of message.fields) {
      if (field.demoValue !== undefined) {
        defaults[field.key] = field.demoValue;
      } else if (field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue;
      } else if (field.dataType === 'boolean') {
        defaults[field.key] = false;
      } else if (field.dataType === 'enum') {
        defaults[field.key] = '';
      } else if (
        ['uint8', 'int8', 'uint16', 'int16', 'uint32', 'int32', 'float32', 'float64'].includes(field.dataType)
      ) {
        defaults[field.key] = 0;
      } else if (field.dataType === 'bytes') {
        defaults[field.key] = [];
      } else {
        defaults[field.key] = '';
      }
    }
    return defaults;
  }, []);

  const resolvePreview = useCallback(
    (
      message: MessageDefinition | null,
      template: MessageTemplate | null,
      base: MessageTemplate | null,
      parameterSet: ParameterSet | null,
      dataset: Dataset | null,
      runtimeOverrides: Record<string, unknown>
    ): void => {
      if (!message) {
        setResolvedPreview(null);
        setResolvedPayloadSource([]);
        return;
      }
      if (!template) {
        const source = [{ source: 'definitionDefault', id: message.id }];
        if (Object.keys(runtimeOverrides).length > 0) {
          source.push({ source: 'runtimeOverride', id: 'manual' });
        }
        setResolvedPreview({
          templateId: message.id,
          messageType: message.messageType,
          messageDefinitionId: message.id,
          resolvedPayload: { ...buildDefaultPayload(message), ...runtimeOverrides },
          appliedSources: source,
        });
        setResolvedPayloadSource(source);
        return;
      }
      const parameterSets = parameterSet
        ? services.parameterSetService.filterForMessageType([parameterSet], message.messageType)
        : [];
      const datasetRow = dataset
        ? services.datasetRowSelector.selectRow(
            dataset,
            template.datasetBindings?.[0]?.strategy ?? DatasetRowStrategy.FIRST,
            0
          )
        : undefined;
      const resolved = services.templateResolver.resolve(template, base ?? undefined, {
        parameterSets,
        dataset: dataset ?? undefined,
        datasetRow,
        runtimeOverrides,
        context: {
          sequence: sequenceNumber,
          timestamp: new Date().toISOString(),
        },
        messageDefinition: message,
      });
      setResolvedPreview(resolved);
      setResolvedPayloadSource(resolved.appliedSources);
    },
    [buildDefaultPayload, services, sequenceNumber]
  );

  const selectMessage = useCallback(
    (message: MessageDefinition) => {
      setSelectedMessage(message);
      const defaults = buildDefaultPayload(message);
      setPayload(defaults);
      setSelectedTemplate(null);
      setSelectedParameterSet(null);
      setSelectedDataset(null);
      setBaseTemplate(null);
      setValidationResult(services.validationService.validate(message, defaults));
      setRawBytes(null);
      setRawHex('');
      setSendError(null);
      setSendSuccess(null);
      resolvePreview(message, null, null, null, null, defaults);
    },
    [buildDefaultPayload, services.validationService, resolvePreview]
  );

  const updatePayload = useCallback(
    (key: string, value: unknown) => {
      setPayload((prev) => {
        const next = { ...prev, [key]: value };
        resolvePreview(selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, selectedDataset, next);
        return next;
      });
    },
    [selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, selectedDataset, resolvePreview]
  );

  const validate = useCallback((): ValidationResult => {
    if (!selectedMessage) {
      const result: ValidationResult = {
        severity: ValidationSeverity.FAIL,
        issues: [
          {
            severity: ValidationSeverity.FAIL,
            code: 'NO_MESSAGE',
            message: '메시지가 선택되지 않았습니다.',
            path: 'message',
          },
        ],
        timestamp: new Date().toISOString(),
      };
      setValidationResult(result);
      return result;
    }
    const result = services.validationService.validate(selectedMessage, payload);
    setValidationResult(result);
    return result;
  }, [selectedMessage, payload, services.validationService]);

  useEffect(() => {
    if (selectedMessage) {
      validate();
    }
  }, [payload, selectedMessage, validate]);

  const loadDemoValues = useCallback(() => {
    if (!selectedMessage) return;
    const demoPayload: Record<string, unknown> = {};
    for (const field of selectedMessage.fields) {
      demoPayload[field.key] = field.demoValue !== undefined ? field.demoValue : field.defaultValue;
    }
    setPayload(demoPayload);
    resolvePreview(selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, selectedDataset, demoPayload);
  }, [selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, selectedDataset, resolvePreview]);

  const resetPayload = useCallback(() => {
    if (!selectedMessage) return;
    const defaults = buildDefaultPayload(selectedMessage);
    setPayload(defaults);
    resolvePreview(selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, selectedDataset, defaults);
  }, [
    selectedMessage,
    selectedTemplate,
    baseTemplate,
    selectedParameterSet,
    selectedDataset,
    buildDefaultPayload,
    resolvePreview,
  ]);

  const selectTemplate = useCallback(
    async (template: MessageTemplate | null) => {
      setSelectedTemplate(template);
      if (!template) {
        setBaseTemplate(null);
        if (selectedMessage) {
          const defaults = buildDefaultPayload(selectedMessage);
          setPayload(defaults);
          resolvePreview(selectedMessage, null, null, selectedParameterSet, selectedDataset, defaults);
        }
        return;
      }
      let base: MessageTemplate | null = null;
      if (template.baseTemplateId) {
        try {
          base = (await messageTemplateRepository.findById(template.baseTemplateId)) ?? null;
        } catch (err) {
          console.warn('Failed to load base template:', err);
        }
      }
      setBaseTemplate(base);
      if (selectedMessage) {
        const resolved = services.templateResolver.resolve(template, base ?? undefined, {
          parameterSets: selectedParameterSet
            ? services.parameterSetService.filterForMessageType([selectedParameterSet], selectedMessage.messageType)
            : [],
          context: {
            sequence: sequenceNumber,
            timestamp: new Date().toISOString(),
          },
          messageDefinition: selectedMessage,
        });
        const nextPayload = { ...resolved.resolvedPayload };
        setPayload(nextPayload);
        resolvePreview(selectedMessage, template, base, selectedParameterSet, selectedDataset, nextPayload);
      }
    },
    [
      selectedMessage,
      selectedParameterSet,
      selectedDataset,
      messageTemplateRepository,
      services,
      buildDefaultPayload,
      resolvePreview,
      sequenceNumber,
    ]
  );

  const selectParameterSet = useCallback(
    (parameterSet: ParameterSet | null) => {
      setSelectedParameterSet(parameterSet);
      resolvePreview(selectedMessage, selectedTemplate, baseTemplate, parameterSet, selectedDataset, payload);
    },
    [selectedMessage, selectedTemplate, baseTemplate, selectedDataset, payload, resolvePreview]
  );

  const selectDataset = useCallback(
    (dataset: Dataset | null) => {
      setSelectedDataset(dataset);
      resolvePreview(selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, dataset, payload);
    },
    [selectedMessage, selectedTemplate, baseTemplate, selectedParameterSet, payload, resolvePreview]
  );

  const refreshAssets = useCallback(async () => {
    if (!selectedProject) return;
    setIsLoadingAssets(true);
    try {
      const [templates, parameterSets, datasets] = await Promise.all([
        messageTemplateRepository.findByProjectId(selectedProject.id),
        parameterSetRepository.findByProjectId(selectedProject.id),
        datasetRepository.findByProjectId(selectedProject.id),
      ]);
      setAvailableTemplates(templates);
      setAvailableParameterSets(parameterSets);
      setAvailableDatasets(datasets);
    } catch (err) {
      console.error('Failed to load generator assets:', err);
    } finally {
      setIsLoadingAssets(false);
    }
  }, [selectedProject, messageTemplateRepository, parameterSetRepository, datasetRepository]);

  const saveAsTemplate = useCallback(
    async (name: string, description?: string): Promise<MessageTemplate | null> => {
      if (!selectedMessage || !selectedProject || !authUser) return null;
      const now = new Date().toISOString();
      const template: MessageTemplate = {
        id: `template-${Date.now()}`,
        projectId: selectedProject.id,
        assetType: TestAssetType.MESSAGE_TEMPLATE,
        name: name.trim() || `Template ${selectedMessage.displayName}`,
        description: description ?? '',
        messageDefinitionId: selectedMessage.id,
        messageType: selectedMessage.messageType,
        payload: { ...payload },
        parameterSetIds: selectedParameterSet ? [selectedParameterSet.id] : [],
        datasetBindings: selectedDataset
          ? [{ datasetId: selectedDataset.id, column: '', fieldKey: '', required: false }]
          : [],
        tags: [],
        version: '1.0',
        revision: 1,
        status: AssetStatus.ACTIVE,
        createdBy: authUser.uid,
        createdAt: now,
        updatedAt: now,
        metadata: {
          savedFrom: 'generator',
        },
      };
      try {
        const saved = await messageTemplateRepository.save(template);
        await refreshAssets();
        return saved;
      } catch (err) {
        console.error('Failed to save template:', err);
        return null;
      }
    },
    [
      selectedMessage,
      selectedProject,
      authUser,
      payload,
      selectedParameterSet,
      selectedDataset,
      messageTemplateRepository,
      refreshAssets,
    ]
  );

  const send = useCallback(
    async (force = false) => {
      if (!selectedMessage || !selectedProject || !authUser) return;

      const result = validate();
      if (result.severity === ValidationSeverity.FAIL && !force) {
        setSendError('Validation FAIL: 전송을 차단했습니다. Force Send는 ADMIN/OPERATOR만 가능합니다.');
        setSnackbarOpen(true);
        return;
      }
      if (result.severity === ValidationSeverity.FAIL && force && !canForceSend) {
        setSendError('Force Send 권한이 없습니다.');
        setSnackbarOpen(true);
        return;
      }

      setIsSending(true);
      setSendError(null);
      setSendSuccess(null);

      try {
        const id = crypto.randomUUID();
        const timestamp = new Date().toISOString();
        const instance: MessageInstance = {
          id,
          messageType: selectedMessage.messageType,
          standard: selectedMessage.standard,
          name: selectedMessage.name,
          source: selectedMessage.sourceRole,
          target: selectedMessage.targetRole,
          direction: Direction.TX,
          timestamp,
          fields: payload,
          projectId: selectedProject.id,
          environment: selectedProject.environment,
          sequenceNumber,
          runId: 'manual',
          messageDefinitionId: selectedMessage.id,
        };

        const encoded = services.encoder.encode(instance);
        const hex = Array.from(encoded)
          .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
          .join(' ');

        const instanceWithRaw = { ...instance, rawBytes: Array.from(encoded), validation: result.severity };

        await services.adapter.connect();
        await services.adapter.publish(instanceWithRaw);
        addMessageToStore(instanceWithRaw);

        const event: MessageEvent = {
          id,
          projectId: selectedProject.id,
          environment: selectedProject.environment,
          runId: 'manual',
          messageDefinitionId: selectedMessage.id,
          messageType: selectedMessage.messageType,
          standard: selectedMessage.standard,
          name: selectedMessage.name,
          source: selectedMessage.sourceRole,
          target: selectedMessage.targetRole,
          sourceRole: selectedMessage.sourceRole,
          targetRole: selectedMessage.targetRole,
          direction: Direction.TX,
          timestamp,
          payload,
          fields: payload,
          rawBytes: Array.from(encoded),
          rawHex: hex,
          validation: result.severity,
          validationStatus: result.severity,
          validationResults: result as unknown as Record<string, unknown>,
          sequenceNumber,
          createdBy: authUser.uid,
          metadata: {
            dictionaryVersion: MESSAGE_DICTIONARY_VERSION,
            encoder: services.encoder.name,
            standard: services.encoder.standard,
            sessionId: resolvedPreview?.templateId ? resolvedPreview.templateId : undefined,
          },
          createdAt: timestamp,
        };

        const isGuestDemo = userProfile?.globalRole === GlobalRole.GUEST && selectedProject.isDemo;
        if (!isGuestDemo) {
          await messageEventRepository.save(event);
        } else {
          console.log('Guest Demo Send: Firestore save skipped (Virtual Only)');
        }

        setRawBytes(encoded);
        setRawHex(hex);
        setSequenceNumber((prev) => prev + 1);
        setSendSuccess({
          messageType: selectedMessage.messageType,
          sequenceNumber,
          validation: result.severity,
        });
        setLastSentEventId(event.id);
        setSnackbarOpen(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setSendError(`전송/저장 실패: ${message}`);
        setSnackbarOpen(true);
      } finally {
        setIsSending(false);
      }
    },
    [
      selectedMessage,
      selectedProject,
      authUser,
      userProfile,
      validate,
      sequenceNumber,
      payload,
      services,
      messageEventRepository,
      addMessageToStore,
      canForceSend,
      resolvedPreview,
    ]
  );

  const clearSendFeedback = useCallback(() => {
    setSendError(null);
    setSendSuccess(null);
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setRecentEvents([]);
      return;
    }
    const unsubscribe = messageEventRepository.subscribeToProject?.(selectedProject.id, (events) => {
      setRecentEvents(events);
    });
    if (unsubscribe) {
      unsubscribeRef.current = unsubscribe;
    } else {
      messageEventRepository
        .findByProjectId(selectedProject.id)
        .then((events) => setRecentEvents(events))
        .catch(() => setRecentEvents([]));
    }
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [messageEventRepository, selectedProject]);

  useEffect(() => {
    refreshAssets();
  }, [refreshAssets]);

  useEffect(() => {
    const defaultMessage = services.dictionaryService.getMessageByType(DEMO_DEFAULT_MESSAGE_TYPE);
    if (defaultMessage) {
      selectMessage(defaultMessage);
    }
  }, [selectMessage, services.dictionaryService]);

  return {
    selectedMessage,
    payload,
    validationResult,
    rawBytes,
    rawHex,
    sequenceNumber,
    isSending,
    recentEvents,
    sendError,
    sendSuccess,
    snackbarOpen,
    lastSentEventId,
    ...services,
    availableTemplates,
    availableParameterSets,
    availableDatasets,
    selectedTemplate,
    selectedParameterSet,
    selectedDataset,
    resolvedPreview,
    resolvedPayloadSource,
    isLoadingAssets,
    selectMessage,
    updatePayload,
    loadDemoValues,
    resetPayload,
    validate,
    send,
    clearSendFeedback,
    closeSnackbar,
    canSend,
    canForceSend,
    isProjectSelected,
    selectTemplate,
    selectParameterSet,
    selectDataset,
    refreshAssets,
    saveAsTemplate,
  };
};
